import React from 'react';
import Box from '@mui/material/Box';
import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemText from '@mui/material/ListItemText';
import Collapse from '@mui/material/Collapse';
import IconButton from '@mui/material/IconButton';
import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';
import { SubmenuContainer } from './MobileDrawer.styles';
import Divider from '../divider/Divider';
import { Typography } from '@mui/material';
import PropTypes from 'prop-types';
import { useTheme } from '@mui/material';

const DrawerSubmenu = ({
  submenu,
  parentKey,
  isTopLevel = false,
  isLastItem,
  handleClick,
}) => {
  const theme = useTheme();
  const [open, setOpen] = React.useState(submenu?.submenus?.length === 1); // Default open if there's only 1 submenu

  const handleCollapseToggle = () => {
    if (submenu?.submenus?.length > 1) {
      setOpen(!open); // Only toggle if there are multiple submenus
    }
  };

  const hasChildren = submenu?.submenus && submenu.submenus.length > 0;
  const hasOneChild = submenu?.submenus && submenu.submenus.length === 1; // Check if there's only one submenu
  const isClickable = submenu?.isClickable !== false;
  const textColor = submenu?.bluecolor ? 'primary.main' : '#000000';
  const contactText = submenu?.contactText || 'Call 1.800.331.7350';

  const handleItemClick = () => {
    if (hasChildren) {
      handleCollapseToggle();
    } else {
      handleClick(submenu);
    }
  };

  return (
    <SubmenuContainer>
      <Box display="flex" flexDirection="column">
        <ListItem
          sx={{
            py: 0.2,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            width: '100%',
            paddingLeft: '1px',
            paddingRight: '16px',
          }}
        >
          {isClickable ? (
            <ListItemButton
              sx={{
                color: textColor,
                flexGrow: 1,
                py: 0,
                padding: 0
              }}
              onClick={handleItemClick}
            >
              {submenu?.acf?.istypography ? (<Typography fontFamily={submenu?.acf?.fontfamily} fontStyle={submenu?.acf?.fontstyle} fontWeight={submenu?.acf?.fontweight}>
                {submenu?.acf?.add_html_content ?
                  <div dangerouslySetInnerHTML={{ __html: submenu?.acf?.html_content }} /> :
                  submenu?.acf?.text_content}
              </Typography>) : (<ListItemText
                primary={submenu.name}
                primaryTypographyProps={{
                  fontFamily: 'body.fontFamily.primary',
                  fontSize: '16px',
                  fontWeight: !isTopLevel ? '500' : '400',
                }}
                sx={{
                  marginBottom: hasOneChild ? 0 : '4px',
                  color: submenu?.acf?.isbutton ? theme.palette.secondary.main : 'inherit',
                }}
              />)}
            </ListItemButton>
          ) : (
            <ListItemText
              primary={submenu?.name}
              primaryTypographyProps={{
                fontFamily: 'body.fontFamily.primary',
                fontSize: '16px',
                fontWeight: !isTopLevel ? '500' : '400',
                color: textColor,
                marginTop: !hasOneChild ? 0 : '4px',
              }}
              sx={{
                paddingLeft: '16px',
                margin: 0
              }}
            />
          )}

          {/* Hide the down arrow if there's only 1 submenu */}
          {isClickable && hasChildren && !hasOneChild && (
            <IconButton sx={{ padding: 0 }} onClick={handleItemClick}>
              {open ? (
                <ExpandLess sx={{ width: '20px', height: '28px' }} />
              ) : (
                <ExpandMore sx={{ width: '20px', height: '28px' }} />
              )}
            </IconButton>
          )}
        </ListItem>

        {/* Divider logic for collapsible items */}
        {!isLastItem && isTopLevel && hasChildren && (
          <Box sx={{ paddingLeft: 0 }}>
            {!open && ( // Show divider initially if the item is not expanded
              <Divider
                backgroundColor='#D9E3F0'
                width="calc(100% - 16px)"
                sidemargin='0px'
              />
            )}
          </Box>
        )}

        {/* Divider logic for non-collapsible items */}
        {!isLastItem && isTopLevel && !hasChildren && (
          <Box sx={{ paddingLeft: 0 }}>
            <Divider
              backgroundColor="#D9E3F0"
              width="calc(100% - 16px)"
              sidemargin='0px'
            />
          </Box>
        )}

        {hasChildren && (
          <Collapse in={open} timeout="auto" unmountOnExit>
            <Box component="div" disablePadding sx={{ color: '#000000' }}>
              {submenu?.submenus?.map((childSubmenu, index) => (
                <DrawerSubmenu
                  key={`${parentKey}-submenu${index + 1}`}
                  submenu={childSubmenu}
                  parentKey={`${parentKey}-submenu${index + 1}`}
                  isTopLevel={false}
                  isLastItem={false}
                  handleClick={handleClick} // Pass handleClick down to submenus
                />
              ))}
              {!isLastItem && (
                <Box sx={{ paddingLeft: 0 }}>
                  <Divider
                    backgroundColor="#D9E3F0"
                    width="calc(100% - 32px)"
                    sidemargin='0px'
                  />
                </Box>
              )}
            </Box>
          </Collapse>
        )}
      </Box>
    </SubmenuContainer>
  );
};


export default DrawerSubmenu;
DrawerSubmenu.propTypes = {
  submenu: PropTypes.shape({
    name: PropTypes.string,
    link: PropTypes.string,
    submenus: PropTypes.arrayOf(
      PropTypes.shape({
        name: PropTypes.string,
        link: PropTypes.string,
        submenus: PropTypes.array,
        isClickable: PropTypes.bool,
        bluecolor: PropTypes.bool,
        contactText: PropTypes.string,
        bold: PropTypes.bool,
      }),
    ),
    isClickable: PropTypes.bool,
    bluecolor: PropTypes.bool,
    contactText: PropTypes.string,
    bold: PropTypes.bool,
  }),
  parentKey: PropTypes.string,
  isTopLevel: PropTypes.bool,
  isLastItem: PropTypes.bool,
  handleClick: PropTypes.func,
};
